import { useState, useEffect } from "react";
import PageHeading from "../PageHeading";
import DynamicSelect from "../DynamicSelect";
import ExportOptions from "../ExportOptions";
import Pagination from "../Shared/Pagination";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const API_ORIGIN = "https://api.muktihospital.com";
const API_KEY =
  process.env.REACT_APP_API_KEY ||
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const money = (n) => (Number(n) || 0).toFixed(2);

// Pharmacy: Income = sale payments (from patients), Expense = purchase payments
// (to suppliers). Net Profit = Income - Expense. Dues are shown separately.
const PharmacyAccounts = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const limit = 10;

  const [filterType, setFilterType] = useState("daily");
  const today = new Date().toISOString().split("T")[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [selectedMonth, setSelectedMonth] = useState(today.slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(today.slice(0, 4));

  const breadcrumbs = [
    { label: "Dashboard", url: "/" },
    { label: "Accounts" },
    { label: "Pharmacy" },
  ];

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  const headers = ["Sl", "Period", "Income", "Purchase (Exp)", "Net Profit", "Sales Due", "Purchase Due"];

  const dataMapper = (() => {
    let counter = 0;
    return (r) => {
      counter += 1;
      return [counter, r.label, money(r.income), money(r.expense), money(r.profit), money(r.salesDue), money(r.purchaseDue)].join("\t");
    };
  })();

  // Fetch sale + purchase for one period, then combine.
  const fetchOne = async (saleUrl, purchaseUrl, label) => {
    const empty = { id: label, label, income: 0, expense: 0, profit: 0, salesDue: 0, purchaseDue: 0 };
    try {
      const [saleRes, purchaseRes] = await Promise.all([
        fetch(saleUrl, { headers: { "Content-Type": "application/json", "x-api-key": API_KEY } }),
        fetch(purchaseUrl, { headers: { "Content-Type": "application/json", "x-api-key": API_KEY } }),
      ]);
      const saleData = await saleRes.json();
      const purchaseData = await purchaseRes.json();

      const bills = Array.isArray(saleData.bills) ? saleData.bills : [];
      const purchases = Array.isArray(purchaseData.purchases) ? purchaseData.purchases : [];

      const income = bills.reduce((s, b) => s + (Number(b.paymentAmount) || 0), 0);
      const salesDue = bills.reduce((s, b) => s + (Number(b.dueAmount) || 0), 0);
      const expense = purchases.reduce((s, p) => s + (Number(p.paymentAmount) || 0), 0);
      const purchaseDue = purchases.reduce((s, p) => s + (Number(p.dueAmount) || 0), 0);

      return { ...empty, income, expense, profit: income - expense, salesDue, purchaseDue };
    } catch (err) {
      console.error("Pharmacy row fetch error:", err);
      return empty;
    }
  };

  const fetchReport = async () => {
    setLoading(true);
    setError("");
    try {
      let jobs = [];

      if (filterType === "daily") {
        const start = new Date(startDate);
        const end = new Date(endDate);
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          const day = new Date(d).toISOString().split("T")[0];
          const saleUrl = `${API_ORIGIN}/api/medicine-bill/bills/daily?startDate=${day}&endDate=${day}`;
          const purchaseUrl = `${API_ORIGIN}/api/medicine-purchase/purchases/daily?startDate=${day}&endDate=${day}`;
          jobs.push(fetchOne(saleUrl, purchaseUrl, new Date(day).toLocaleDateString()));
        }
      } else if (filterType === "monthly") {
        const [year] = selectedMonth.split("-");
        for (let m = 1; m <= 12; m++) {
          const saleUrl = `${API_ORIGIN}/api/medicine-bill/bills/monthly?year=${year}&month=${m}`;
          const purchaseUrl = `${API_ORIGIN}/api/medicine-purchase/purchases/monthly?year=${year}&month=${m}`;
          const label = new Date(year, m - 1, 1).toLocaleString("default", { month: "long", year: "numeric" });
          jobs.push(fetchOne(saleUrl, purchaseUrl, label));
        }
      } else {
        const saleUrl = `${API_ORIGIN}/api/medicine-bill/bills/yearly?year=${selectedYear}`;
        const purchaseUrl = `${API_ORIGIN}/api/medicine-purchase/purchases/yearly?year=${selectedYear}`;
        jobs.push(fetchOne(saleUrl, purchaseUrl, selectedYear));
      }

      const result = await Promise.all(jobs);
      setRows(result);
      setCurrentPage(1);
    } catch (err) {
      setError("Unable to connect to the server. Please try again later.");
      console.error("Pharmacy report fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, startDate, endDate, selectedMonth, selectedYear]);

  const totals = rows.reduce(
    (a, r) => ({
      income: a.income + r.income,
      expense: a.expense + r.expense,
      profit: a.profit + r.profit,
      salesDue: a.salesDue + r.salesDue,
      purchaseDue: a.purchaseDue + r.purchaseDue,
    }),
    { income: 0, expense: 0, profit: 0, salesDue: 0, purchaseDue: 0 }
  );

  const totalPages = Math.ceil(rows.length / limit) || 1;
  const pagedRows = rows.slice((currentPage - 1) * limit, currentPage * limit);
  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  return (
    <div>
      <PageHeading title="Pharmacy" breadcrumbs={breadcrumbs} />
      <div className="bg-white shadow-sm rounded-lg">
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
         <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700">Pharmacy Accounts Report</h2>
         </div>
          <div className="flex items-center gap-2">
            <label className="label mb-0 shrink-0">Filter By:</label>
            <div className="w-32 shrink-0">
              <DynamicSelect
                options={filterOptions}
                onChange={(option) => setFilterType(option.value)}
                value={filterOptions.find((opt) => opt.value === filterType)}
                isSearchable={false}
                controlBackgroundColor="#f7fafc"
                controlBorderColor="#cbd5e0"
              />
            </div>
            {filterType === "daily" && (
              <>
                <label className="label mb-0 shrink-0">From:</label>
                <input type="date" className="input-field" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
                <label className="label mb-0 shrink-0">To:</label>
                <input type="date" className="input-field" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </>
            )}
            {filterType === "monthly" && (
              <input type="month" className="input-field" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} />
            )}
            {filterType === "yearly" && (
              <input type="number" className="input-field w-28" value={selectedYear} min="2000" max="2099" onChange={(e) => setSelectedYear(e.target.value)} />
            )}
            <ExportOptions
              data={rows}
              title="Pharmacy Accounts Report"
              headers={headers}
              dataMapper={dataMapper}
              dropdownOpen={dropdownOpen}
              toggleDropdown={() => setDropdownOpen((p) => !p)}
            />
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <p className="text-center text-gray-500 py-10">Loading report...</p>
          ) : error ? (
            <p className="text-center text-red-500 py-10">{error}</p>
          ) : (
            <>
              {/* Summary cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <span className="absolute left-0 top-0 h-full w-1 bg-emerald-500" />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Income (Sale)</p>
                  <p className="text-xl font-bold text-emerald-600 mt-1.5">৳ {money(totals.income)}</p>
                </div>
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <span className="absolute left-0 top-0 h-full w-1 bg-rose-500" />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Purchase (Exp)</p>
                  <p className="text-xl font-bold text-rose-600 mt-1.5">৳ {money(totals.expense)}</p>
                </div>
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <span className="absolute left-0 top-0 h-full w-1 bg-M-primary-color" />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Net Profit</p>
                  <p className={`text-xl font-bold mt-1.5 ${totals.profit < 0 ? "text-rose-600" : "text-M-heading-color"}`}>৳ {money(totals.profit)}</p>
                </div>
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <span className="absolute left-0 top-0 h-full w-1 bg-amber-500" />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Sales Due</p>
                  <p className="text-xl font-bold text-amber-600 mt-1.5">৳ {money(totals.salesDue)}</p>
                </div>
                <div className="relative overflow-hidden bg-white border border-gray-100 rounded-xl p-4 shadow-sm">
                  <span className="absolute left-0 top-0 h-full w-1 bg-orange-500" />
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">Purchase Due</p>
                  <p className="text-xl font-bold text-orange-600 mt-1.5">৳ {money(totals.purchaseDue)}</p>
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-[900px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-[#FBFBFD]">
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Sl</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
                        {filterType === "daily" ? "Date" : filterType === "monthly" ? "Month" : "Year"}
                      </th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-green-700 p-3">Income</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-red-700 p-3">Purchase Exp</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-blue-700 p-3">Net Profit</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-amber-700 p-3">Sales Due</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-orange-700 p-3">Purchase Due</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedRows.length > 0 ? (
                      pagedRows.map((r, index) => (
                        <tr key={r.id} className="hover:bg-gray-50 border-b">
                          <td className="text-left font-inter text-sm text-M-heading-color p-3">{(currentPage - 1) * limit + index + 1}</td>
                          <td className="text-left font-inter text-sm text-M-heading-color p-3">{r.label}</td>
                          <td className="text-right font-inter text-sm text-green-600 p-3">{money(r.income)}</td>
                          <td className="text-right font-inter text-sm text-red-600 p-3">{money(r.expense)}</td>
                          <td className="text-right font-inter text-sm font-semibold text-blue-700 p-3">{money(r.profit)}</td>
                          <td className="text-right font-inter text-sm text-amber-600 p-3">{money(r.salesDue)}</td>
                          <td className="text-right font-inter text-sm text-orange-600 p-3">{money(r.purchaseDue)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="7" className="text-center p-4 text-gray-500">No pharmacy data found</td></tr>
                    )}

                    {rows.length > 0 && (
                      <tr className="border-t-2 border-M-text-color/30 bg-gray-50 font-bold">
                        <td className="text-right font-inter text-sm text-M-heading-color p-3" colSpan={2}>Total:</td>
                        <td className="text-right font-inter text-sm text-green-700 p-3">{money(totals.income)}</td>
                        <td className="text-right font-inter text-sm text-red-700 p-3">{money(totals.expense)}</td>
                        <td className="text-right font-inter text-sm text-blue-700 p-3">{money(totals.profit)}</td>
                        <td className="text-right font-inter text-sm text-amber-700 p-3">{money(totals.salesDue)}</td>
                        <td className="text-right font-inter text-sm text-orange-700 p-3">{money(totals.purchaseDue)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalRecords={rows.length}
                itemsPerPage={limit}
                onPageChange={handlePageChange}
              />
            </>
          )}
        </div>
        <ToastContainer />
      </div>
    </div>
  );
};

export default PharmacyAccounts;